import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import styles from "./AgendaItem.module.css";
import ViewPdfButton from "../ViewPdfButton";
import AddNoteButton from "../AddNoteButton";
import editSvg from "../EditButton/edit.svg";
import AddNoteItem from "../AddNoteItem";
import ApprovedIcon from "../../assets/Approved_Gray.svg";
import NotificationIcon from "../../assets/Notification_Icon.svg";
import classNames from "classnames";
import { markdownParse } from "../../utils/markdownParse";
import LockIcon from "../LockIcon";
import Markdown from "../Markdown";
import { toTitleCase } from "shared/@utils/formatString";
import { BlobProvider, pdf } from "@react-pdf/renderer";
import AttendancePdf from "ManagementTeam/AgendaPage/AttendancePdf";
import PDF from "./pdf_Icon.svg";
import dayjs from "dayjs";
import { convertBlobToBase64 } from "ManagementTeam/utils/blobParse";
import { useAgenda } from "ManagementTeam/AgendaPage/useAgenda";
import { useDocuments } from "ManagementTeam/DocumentsPage/useDocuments";

/**
 * A list item that allows the user to view agenda items for a meeting and can allow the user to vote on the items that require their decision.
 */
export default function AgendaItem({
  id,
  assignee,
  assignedType,
  title,
  description,
  isNew,
  isCompleted,
  votingStatus,
  isLocked,
  onSaveNote,
  onDeleteNote,
  note,
  pdfName,
  onClickPdf,
  isAttendanceAgenda = false,
  attendanceItem = null,
  filesAttendance = [],
  onClickAttendancePdf,
}) {
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [localNote, setLocalNote] = useState(note);
  const [showPDFAttendance, setShowPDFAttendance] = useState(false);
  const [pdfAttendance, setPDFAttendance] = useState();
  const { postAttendancePDF } = useAgenda();

  useEffect(() => {
    if (id && pdfAttendance && filesAttendance.length === 0) {
      const payload = {
        agendaId: id,
        files: {
          name: "Attendance PDF",
          data: pdfAttendance,
          mimetype: "pdf",
        },
      };
      postAttendancePDF(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfAttendance]);
  return (
    <div className={styles.agendaItemContainer}>
      <div className={styles.agendaItem}>
        {/* Agenda Item Details */}
        <div className={styles.leftSide}>
          <div
            className={
              assignedType === "Attorney"
                ? classNames(styles.leftBar, styles.attorney)
                : assignedType === "WWG"
                ? classNames(styles.leftBar, styles.wwg)
                : classNames(styles.leftBar, styles.diamond)
            }
          >
            {isNew ? (
              <img
                className={styles.notificationIcon}
                src={NotificationIcon}
                alt="Notification Icon"
              />
            ) : null}
            {isCompleted ? (
              <img
                className={styles.approvedIcon}
                src={ApprovedIcon}
                alt="Approved Icon"
              />
            ) : null}
          </div>
          <div className={styles.agendaDetails}>
            <div className={styles.grid}>
              <p
                className={classNames(
                  styles.assignee,
                  styles.transition,
                  !isCreatingNote && !localNote
                    ? styles.light
                    : styles.visibleNone
                )}
              >
                {toTitleCase(assignee)}
              </p>
              <p
                className={classNames(
                  styles.assignee,
                  styles.transition,
                  isCreatingNote || localNote
                    ? styles.regular
                    : styles.visibleNone
                )}
              >
                {toTitleCase(assignee)}
              </p>
            </div>
            <div className={styles.grid}>
              <p
                className={classNames(
                  styles.title,
                  styles.transition,
                  isCreatingNote || localNote
                    ? styles.light
                    : styles.visibleNone
                )}
              >
                {title}
              </p>
              <p
                className={classNames(
                  styles.transition,
                  !isCreatingNote && !localNote
                    ? styles.regular
                    : styles.visibleNone
                )}
              >
                {title}
              </p>
            </div>
            <div
              className={description ? styles.description : styles.displayNone}
            >
              {description ? <Markdown content={description} /> : null}
            </div>
            <div
              className={
                pdfName && onClickPdf ? styles.pdfButton : styles.displayNone
              }
            >
              <ViewPdfButton
                pdfName={pdfName}
                onClick={() => {
                  onClickPdf();
                }}
              />
            </div>
            {isAttendanceAgenda &&
              filesAttendance.length === 0 &&
              showPDFAttendance && (
                <BlobProvider
                  document={<AttendancePdf attendanceItem={attendanceItem} />}
                  fileName={`${dayjs().format("MM_DD_YYYY")} Agenda`}
                >
                  {({ blob, url, loading, error }) => {
                    if (blob) {
                      convertBlobToBase64(blob).then((res) => {
                        setPDFAttendance(res);
                      });
                    }
                    return (
                      <div>
                        {loading ? (
                          <div style={{ cursor: "progress" }}>
                            <span>PDF is loading...</span>
                          </div>
                        ) : (
                          <a
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "var(--color-alt-text)",
                              filter: "var(--icon-filter)",
                              paddingTop: "10px",
                            }}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img src={PDF} alt="pdf icon" />
                            <div
                              style={{
                                paddingLeft: "10px",
                                color: "var(--color-alt-text)",
                                fontSize: "14px",
                              }}
                            >
                              Attendance PDF
                            </div>
                          </a>
                        )}
                      </div>
                    );
                  }}
                </BlobProvider>
              )}
            {isAttendanceAgenda &&
              filesAttendance.length === 0 &&
              !showPDFAttendance && (
                <button
                  className={styles.getAttendanceBtn}
                  onClick={() => setShowPDFAttendance(true)}
                >
                  Get Attendance
                </button>
              )}
            {isAttendanceAgenda && filesAttendance.length > 0 && (
              <div
                className={
                  filesAttendance[0]?.name && onClickPdf
                    ? styles.pdfButton
                    : styles.displayNone
                }
              >
                <ViewPdfButton
                  pdfName={filesAttendance[0]?.name}
                  onClick={() => {
                    onClickAttendancePdf();
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {/* Voting Status */}
        <div className={styles.rightSide}>
          {isLocked && <LockIcon />}
          {votingStatus === "Passed" ? (
            <p className={classNames(styles.statusText, styles.passed)}>
              Passed!
            </p>
          ) : null}
          {votingStatus === "Failed" ? (
            <p className={classNames(styles.statusText, styles.failed)}>
              Failed!
            </p>
          ) : null}
          {votingStatus === "Voting in Progress" ? (
            <p className={classNames(styles.statusText, styles.pending)}>
              Voting in Progress
            </p>
          ) : null}
          {votingStatus === "Upcoming" ? (
            <p className={classNames(styles.statusText, styles.upcoming)}>
              Upcoming
            </p>
          ) : null}
        </div>
      </div>
      {/* Note Creating Section */}
      <div className={styles.noteFlex}>
        <div className={styles.grid}>
          <div
            className={
              !localNote ? styles.addNoteButton : styles.visibleNoneNote
            }
          >
            <AddNoteButton
              onClick={() => {
                setIsCreatingNote(true);
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setIsCreatingNote(true);
            }}
            className={
              localNote ? styles.editNoteButton : styles.visibleNoneNote
            }
          >
            <img
              src={editSvg}
              alt="Note"
              className={classNames(styles.editNoteButtonImage)}
            />
            <span className={classNames(styles.editNoteButtonText)}>
              EDIT NOTE
            </span>
          </button>
        </div>
        <div className={styles.grid}>
          <div
            className={classNames(styles.transition, styles.noteDiv, {
              [styles.visibleNoneNote]: !isCreatingNote,
            })}
          >
            <div className={styles.noteItem}>
              <AddNoteItem
                note={localNote ? localNote : null}
                onSave={(n) => {
                  setLocalNote(n);
                  setIsCreatingNote(false);
                  onSaveNote(n);
                }}
                onDelete={
                  localNote
                    ? () => {
                        setLocalNote(null);
                        setIsCreatingNote(false);
                        onDeleteNote("");
                      }
                    : null
                }
                onCancel={() => {
                  setIsCreatingNote(false);
                }}
              />
            </div>
          </div>
          <p
            className={classNames(styles.transition, styles.note, {
              [styles.visibleNone]: !(localNote && !isCreatingNote),
            })}
          >
            {localNote && !isCreatingNote ? localNote : null}
          </p>
        </div>
      </div>
    </div>
  );
}

AgendaItem.propTypes = {
  /**
   * Assignee of the decision (Bold Text).
   */
  assignee: PropTypes.string,
  /**
   * The type of assigned item, either "Diamond”, "WWG”, or "Attorney”.
   */
  assignedType: PropTypes.string,
  /**
   * Title of the decision item (Regular Text) .
   */
  title: PropTypes.string,
  /**
   * Description of the details of the decision item (Italic Text).
   */
  description: PropTypes.string,
  /**
   * Boolean that holds if the item is new to the list.
   */
  isNew: PropTypes.bool,
  /**
   * Boolean that holds if the item is completed. A checkmark will show on the left if it is.
   */
  isCompleted: PropTypes.bool,
  /**
   * The current voting status of the decision item. (Passed, Failed, Pending, or Upcoming)
   */
  votingStatus: PropTypes.string,
  /**
   * Function that’s used to open the modal of the decision item that belongs to that agenda item.
   */
  onOpenModal: PropTypes.func,
  /**
   * Function that’s used when the user needs to save a note.
   */
  onSaveNote: PropTypes.func,
  /**
   * Function that’s used when the user needs to delete a note.
   */
  onDeleteNote: PropTypes.func,
  /**
   * A string that holds an existing note.
   */
  note: PropTypes.string,
  /**
   * A string that holds the name of the PDF file for the View PDF Button.
   */
  pdfName: PropTypes.string,
  /**
   * Function that’s used to view the PDF when clicking the View PDF Button.
   */
  onClickPdf: PropTypes.func,
};
